import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {ObjectTypes, ApiErrorCodes, SortDirections} from '../../../api/consts';
import {useTranslation} from 'react-i18next';
import {push as navigateTo} from 'connected-react-router';
import {useGenericResultsDataApi} from '../../../api/hooks';
import {useSMCSnackbar, useColumnsWidthsPersistedState, useSort, useColumnsStatePersistedState} from '../../../utils/hooks';
import {SMCPaths} from '../../../conf/Routes';
import {Link} from 'react-router-dom';
import store from '../../../store';
import {UserGetListSchema, UserStatuses} from '../../../api/DBSchema/User';
import EditObjectCell from '../../../components/core/SMCTable/CommonCells/EditObjectCell';
import ToExcelSnackbarContent from '../../PopupPages/ToExcelSnackbarContent';
import connectedLogo from '../../../assets/images/gateway/connected.png';
import disconnectedLogo from '../../../assets/images/gateway/disconnected.png';
import disabledLogo from '../../../assets/images/gateway/disabled.png';
import DescriptionCell from '../../../components/core/SMCTable/CommonCells/DescriptionCell';
import {useSnackbar} from 'notistack';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {buildStringSortCaseInsensitive} from '../../../utils/sortUtils';

const roleCell = React.memo(({value}) => {
    return (
        value ? <Link to={`${SMCPaths.roles}?id=${value}`}>{value}</Link> : null
    )
});

const statusCell = React.memo(({value}) => {
    const { t } = useTranslation();
    switch (value) {
        case UserStatuses.connected:
            return <img src={connectedLogo} title={t('SMC_GATEWAY_STATUS_CONNECTED')} alt="Connected" />
        case UserStatuses.disconnected:
            return <img src={disconnectedLogo} title={t('SMC_GATEWAY_STATUS_DISCONNECTED')} alt="Disconnected" />
        case UserStatuses.disabled:
            return <img src={disabledLogo} title={t('SMC_GEN_DISABLED')} alt="Disabled" />
        default:
            return null;
    }
});


export function useUserResults() {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const { enqueueSnackbar } = useSnackbar();
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSMCSnackbar();
    const { sortBasis, sortDirection, setSort } = useSort('name', SortDirections.ASCENDING);

    const columns = useMemo(() => ([
        { id: 'name',        accessor: UserGetListSchema.name,        header: t('SMP.NAME'),                                               width: 150,                  defaultShow: true, sort: buildStringSortCaseInsensitive(UserGetListSchema.name),        Cell: EditObjectCell  },
        { id: 'description', accessor: UserGetListSchema.description, header: t('SMP.DESCRIPTION'), width: 32,  align: 'center', defaultShow: true, sort: buildStringSortCaseInsensitive(UserGetListSchema.description), Cell: DescriptionCell },
        { id: 'firstName',   accessor: UserGetListSchema.firstName,   header: t('SMP.USERS.TABLE.FIRST_NAME'),                             width: 115,                  defaultShow: true, sort: buildStringSortCaseInsensitive(UserGetListSchema.firstName),                         },
        { id: 'lastName',    accessor: UserGetListSchema.lastName,    header: t('SMP.USERS.TABLE.LAST_NAME'),                              width: 115,                  defaultShow: true, sort: buildStringSortCaseInsensitive(UserGetListSchema.lastName),                          },
        { id: 'company',     accessor: UserGetListSchema.company,     header: t('SMP.USERS.TABLE.COMPANY'),                                width: 120,                  defaultShow: true, sort: buildStringSortCaseInsensitive(UserGetListSchema.company),                           },
        { id: 'email',       accessor: UserGetListSchema.email,       header: t('SMP.USERS.TABLE.EMAIL'),                                  width: 170,                  defaultShow: true, sort: buildStringSortCaseInsensitive(UserGetListSchema.email),                             },
        { id: 'phone',       accessor: UserGetListSchema.phone,       header: t('SMP.USERS.TABLE.PHONE'),                                  width: 110,                  defaultShow: true, sort: buildStringSortCaseInsensitive(UserGetListSchema.phone),                             },
        { id: 'role',        accessor: UserGetListSchema.role,        header: t('SMP.USERS.TABLE.ROLE'),                                   width: 100,                  defaultShow: true, sort: buildStringSortCaseInsensitive(UserGetListSchema.role),        Cell: roleCell        },
        { id: 'status',      accessor: UserGetListSchema.status,      header: t('SMP.USERS.TABLE.STATUS'),                                 width: 55,  align: 'center', defaultShow: true, sort: buildStringSortCaseInsensitive(UserGetListSchema.status),      Cell: statusCell      }
    ]), [t,]);
    
    const rowIdSelector = obj => obj[UserGetListSchema.name];

    const defaultColumnsState = useMemo(() => (
        columns.map(column => ({
            id: column.id,
            show: Boolean(column.defaultShow)
        }))
    ), [columns]);
    const [columnsState, setColumnsState] = useColumnsStatePersistedState(ObjectTypes.USER, defaultColumnsState);
    const setDefaultColumnsState = useCallback(() => setColumnsState(defaultColumnsState), [defaultColumnsState, setColumnsState]);

    const [columnsWidths, setColumnsWidths] = useColumnsWidthsPersistedState(ObjectTypes.USER, null);

    const [isPageLoaded, setIsPageLoaded] = useState(false);
    const isFirstRender = useRef(true);

    const {
        tableContent,
        isFetching,
        fetchNextPage,
        isLastPage,
        reloadTable,
        reloadTableNoClearSelected,
        selectedObjects,
        setSelectedObjects,
        deleteSelectedObjects,
        filterObject,
        setFilterObject,
        freeTextSearch
    } = useGenericResultsDataApi(ObjectTypes.USER);

    const newObjectCallback = useCallback((objectId, errorCode) => {
        if (errorCode) {
            alert(t(errorCode));
            // enqueueErrorSnackbar(t(errorCode));
        } else if (objectId) {
            store.dispatch(navigateTo({search: 'id=' + objectId}));
        }
    }, [t]);

    const reloadButtonOnClick = useCallback(() => {
        reloadTable()
            .catch(errorCode => {
                enqueueErrorSnackbar(t(errorCode));
            });
    }, [reloadTable, enqueueErrorSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        if (window.confirm(t('RESULTS_OBJECTS_DELETE_QUES'))) {
            setIsPageLoaded(false);
            deleteSelectedObjects()
                    .then(({successMessage, errorMessage}) => {
                        successMessage && enqueueSuccessSnackbar(successMessage);
                        errorMessage && enqueueErrorSnackbar(errorMessage);
                        reloadTable();
                    })
                    .catch(e => {
                        const errorMessage = getErrorMessage(e);
                        enqueueErrorSnackbar(errorMessage);
                    })
                    .finally(() => {
                        setIsPageLoaded(true);
                    });
        }
    }, [selectedObjects, deleteSelectedObjects, reloadTable, enqueueSuccessSnackbar, enqueueErrorSnackbar, getErrorMessage, t]);

    const toExcelAction = useCallback(() => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        enqueueSnackbar(null, {
            persist: true,
            content: (key) => (
                <ToExcelSnackbarContent
                    list={selectedObjects}
                    objectType="USER"
                    id={key}
                />
            )
        })
    }, [selectedObjects, enqueueSnackbar, t]);


    // Reload table on first time loading
    useEffect(() => {
        reloadTable()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTable,  enqueueErrorSnackbar, t]);

    // Reload table without clearing selected on filter changed
    useEffect(() => {
        // Don't run on first render, only when filter is changed
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        reloadTableNoClearSelected()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTableNoClearSelected, filterObject, enqueueErrorSnackbar, t]);    

    return ({
        tableContent,
        isFetching,
        isPageLoaded,
        filterObject,
        setFilterObject,
        freeTextSearch,
        fetchNextPage,
        isLastPage,
        reloadTable,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        columnsWidths,
        setColumnsWidths,
        rowIdSelector,
        selectedObjects,
        setSelectedObjects,
        newObjectCallback,
        reloadButtonOnClick,
        deleteButtonOnClick,
        toExcelAction
    });
}